import { createSelector } from "reselect"
import { chain, sortBy, get, map, includes, size } from "lodash"
import { getContentSets } from "selectors/securities-table"
import { getContentSetsFactors } from "selectors/contentSets"
import { getIsOpPro } from "modules/account-root/selectors"
import { computeChipSecuritiesIds } from "helpers/filters/chips"
import { NUMERIC_AND_EXISTENCE_OPERATORS } from "helpers/filters/filter-ons/existence"
import { NUMERIC_OPERATORS } from "helpers/filters/filter-ons/numbers"
import { STRING_OPERATORS } from "helpers/filters/filter-ons/strings"
import { CLASSIFICATION_OPERATORS } from "helpers/filters/filter-ons/classifications"
import { getMissingDataFiles, betasAreMissing } from "selectors/missingDataFiles"
import { computeClassifications } from "helpers/classification"
import { getModelCategories, getModelFactors } from "selectors/modelMetadata"
import { computeAddedFilters } from "../helpers"
import { computeFactorCategoryBasedFilters } from "../helpers"
import { flattenClassificationValues } from "resources/classifications"

const getModelCountries = state => state.global.data.modelCountries.data
const getModelCurrencies = state => state.global.data.modelCurrencies.data
const getSecuritiesDescriptors = state => get(state, "global.data.securityFinderSecuritiesDescriptors.data")
const getRawClassifications = (state) => get(state, "global.data.classifications.data.classifications")
export const getClassificationsValues = (state) => get(state, "global.data.classificationsValues.data")
export const getFilter = (_, props) => props.filter
export const getCurrency = (_, props) => props.currency
const getEtfsList = (_, props) => props.etfsList
const getPortfoliosList = (_, props) => props.portfoliosList
const getWatchlistsList = (_, props) => props.watchlistsList
const getSwapsList = (_, props) => props.swapsList
const getIndexesList = (_, props) => props.indexesList
const getModelId = (_, props) => props.modelId
const getSelectedDate = (_, props) => props.selectedDate
const getIsHistoricalFactorSensitivityAvailable = (_, props) => props.isHistoricalFactorSensitivityAvailable

export const getClassifications = createSelector(
  [getRawClassifications, getSelectedDate],
  computeClassifications,
)

const getBetaFilters = createSelector(
  [getFilter, getMissingDataFiles, getModelId],
  (currentFilter, allMissingDataFiles, modelId) => {
    const missingDataFiles = get(allMissingDataFiles, modelId)
    if (!allMissingDataFiles || betasAreMissing(missingDataFiles, modelId)) { return [] }

    const options = []
    if (!includes(missingDataFiles, "historicalBeta")) {
      options.push({
        label: "Historical",
        type: "beta",
        dataType: "numeric",
        operators: NUMERIC_OPERATORS,
        value: "historical",
        isDisabled: !!get(currentFilter, "beta.historical"),
      })
    }
    if (!includes(missingDataFiles, "predictedBeta")) {
      options.push({
        label: "Predicted",
        type: "beta",
        dataType: "numeric",
        operators: NUMERIC_OPERATORS,
        value: "predicted",
        isDisabled: !!get(currentFilter, "beta.predicted"),
      })
    }
    return [{
      label: "Beta",
      type: "beta",
      options,
    }]
  },
)

export const getFilterOns = createSelector(
  [getModelCategories, getModelFactors, getModelCountries, getModelCurrencies, getFilter, getContentSets, getBetaFilters, getClassifications, getClassificationsValues, getIsOpPro, getIsHistoricalFactorSensitivityAvailable],
  (categories, factors, countries, currencies, currentFilter, contentSets, betaFilters, classifications, classificationValues, isOpPro, isHistoricalFactorSensitivityAvailable) => {
    const classificationFilters = []
    let riskFilters = []

    if (!isOpPro) {
      riskFilters = [{
        label: "Risk",
        type: "risk",
        options: [{
          label: "Total Risk",
          type: "risk",
          dataType: "percent",
          operators: NUMERIC_OPERATORS,
          value: "TOTAL",
        }, {
          label: "Factor Risk",
          type: "risk",
          dataType: "percent",
          operators: NUMERIC_OPERATORS,
          value: "VAR_DECOMP_FACTORS",
        }, {
          label: "Specific Risk",
          type: "risk",
          dataType: "percent",
          operators: NUMERIC_OPERATORS,
          value: "VAR_DECOMP_SPECIFIC",
        }],
      }]
    }

    if (size(classifications) > 0) {
      const classificationFilter = {
        label: "Classification",
        type: "classification",
        options: [],
      }
      classificationFilters.push(classificationFilter)

      for (const classification of classifications) {
        const versionValues = get(classificationValues, `${classification.id}.version.values`)
        const tiers = classification.version.tiers
        const options = flattenClassificationValues(tiers, versionValues, 1)
        classificationFilter.options.push({
          label: `${classification.name}`,
          isDisabled: chain(currentFilter)
            .get("classification")
            .some(filter => filter.id === classification.id)
            .value(),
          type: "classification",
          dataType: "string",
          operators: CLASSIFICATION_OPERATORS,
          options: sortBy(options, "label"),
          value: classification.id,
        })
      }
    }

    const filters = [{
      label: "Descriptors",
      type: "descriptor",
      options: [{
        label: "Country",
        type: "descriptor",
        dataType: "string",
        operators: STRING_OPERATORS,
        value: "country",
        options: countries,
      }, {
        label: "Currency",
        type: "descriptor",
        dataType: "string",
        operators: STRING_OPERATORS,
        value: "currency",
        options: currencies,
      }, {
        label: "Asset Class",
        type: "descriptor",
        dataType: "string",
        operators: STRING_OPERATORS,
        value: "assetClass",
        options: [{
          value: "commodity",
          label: "Commodity",
        }, {
          value: "currency",
          label: "Currency",
        }, {
          value: "equity",
          label: "Equity",
        }, {
          value: "fixedIncome",
          label: "Fixed Income",
        }, {
          value: "index",
          label: "Index",
        }, {
          value: "swap",
          label: "Basket",
        }],
      }, {
        label: "Asset Subclass",
        type: "descriptor",
        dataType: "string",
        operators: STRING_OPERATORS,
        value: "assetSubclass",
        options: [{
          value: "depositoryReceipt",
          label: "Depository Receipt",
        }, {
          value: "etf",
          label: "ETF",
        }, {
          value: "stock",
          label: "Stock",
        }],
      }, {
        label: "Average Daily Volume",
        type: "descriptor",
        dataType: "millions",
        operators: NUMERIC_OPERATORS,
        value: "averageDailyVolume",
      }, {
        label: "Market Cap",
        type: "descriptor",
        dataType: "millions",
        operators: NUMERIC_OPERATORS,
        value: "marketCapitalization",
      }],
    },
    ...classificationFilters,
    ...betaFilters,
    ...riskFilters,
    ...computeFactorCategoryBasedFilters(categories, factors, currentFilter, isOpPro, isHistoricalFactorSensitivityAvailable),
    ]

    for (const contentSet of contentSets) {
      filters.push(...map(contentSet.categories, ({ name, id }) => ({
        label: `${contentSet.name} ${name} Exposure`,
        type: "factorExposure",
        options: chain(contentSet.factors)
          .pickBy(({ category }) => category === id)
          .map(({ name, id }) => ({
            label: `${name} Exposure`,
            type: "factorExposure",
            dataType: "numeric",
            operators: NUMERIC_AND_EXISTENCE_OPERATORS,
            value: id,
            contentSetId: contentSet.id,
          }))
          .value(),
      })))
    }

    return filters
  },
)

export const getChipSecuritiesIds = createSelector(
  [getFilter],
  computeChipSecuritiesIds,
)

export const getAddedFilters = createSelector(
  [getFilter, getFilterOns, getModelFactors, getSecuritiesDescriptors, getContentSetsFactors, getClassifications, getClassificationsValues, getCurrency, getPortfoliosList, getEtfsList, getWatchlistsList, getSwapsList, getIndexesList],
  (filter, filterOns, factorsMetadata, securitiesDescriptors, contentSetsFactors, classificationsMetas, classificationsValues, currency, portfolios, etfs, watchlists, swaps, indexes) => {
    const combinedFactorsMetaData = { ...factorsMetadata, ...contentSetsFactors }
    return computeAddedFilters(filter, filterOns, {
      securitiesDescriptors,
      combinedFactorsMetaData,
      classificationsInfos: { classificationsMetas, classificationsValues },
      currency,
    }, portfolios, etfs, watchlists, swaps, indexes)
  },
)
